import type {
  CloseOrderRevisionSessionRequest,
  OrderRevisionBoardPageResponse,
  OrderRevisionRequestCategory,
  OrderRevisionSession,
  ReplyOrderRevisionRequestRequest,
} from "../../../domain/types/order-revision-request";
import { httpClient } from "../http-client";

export class CompanyOrderRevisionRequestApiService {
  /**
   * 세션 게시판 메인 (세션마다 1행)
   */
  async getOrderRevisionSessions(
    companyId: number,
    params: {
      category?: OrderRevisionRequestCategory;
      closed?: boolean;
      keyword?: string;
      page?: number;
      size?: number;
    }
  ): Promise<OrderRevisionBoardPageResponse> {
    const queryParams = new URLSearchParams();
    if (params.category) queryParams.append("category", params.category);
    if (params.closed !== undefined)
      queryParams.append("closed", params.closed.toString());
    if (params.keyword) queryParams.append("keyword", params.keyword);
    if (params.page !== undefined)
      queryParams.append("page", params.page.toString());
    if (params.size !== undefined)
      queryParams.append("size", params.size.toString());

    const url = `/api/company/${companyId}/revision-requests?${queryParams.toString()}`;

    const response = await httpClient.get<{
      data: {
        items: OrderRevisionBoardPageResponse["content"];
        page: number;
        size: number;
        totalElements: number;
        totalPages: number;
      };
      errorMsg: string | null;
      errorCode: string | null;
    }>(url);

    if (response.data.errorCode) {
      throw new Error(
        response.data.errorMsg || "주문 문의 목록을 가져오지 못했습니다."
      );
    }

    const apiData = response.data.data;
    return {
      content: apiData.items || [],
      page: apiData.page,
      size: apiData.size,
      totalElements: apiData.totalElements,
      totalPages: apiData.totalPages,
    };
  }

  /**
   * 세션 상세 조회 — sessionId 기반 (채팅형, 메시지 시간순)
   */
  async getOrderRevisionSessionDetail(
    companyId: number,
    sessionId: number
  ): Promise<OrderRevisionSession> {
    const url = `/api/company/${companyId}/revision-requests/${sessionId}`;

    const response = await httpClient.get<{
      data: OrderRevisionSession;
      errorMsg: string | null;
      errorCode: string | null;
    }>(url);

    if (response.data.errorCode) {
      throw new Error(
        response.data.errorMsg || "문의 내역을 가져오지 못했습니다."
      );
    }

    return response.data.data;
  }

  /**
   * 메시지 전송 — sessionId 기반, 활성 세션에만 가능
   */
  async sendAdminMessage(
    companyId: number,
    sessionId: number,
    request: ReplyOrderRevisionRequestRequest
  ): Promise<OrderRevisionSession> {
    const url = `/api/company/${companyId}/revision-requests/${sessionId}/messages`;

    const response = await httpClient.post<{
      data: OrderRevisionSession;
      errorMsg: string | null;
      errorCode: string | null;
    }>(url, request);

    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "메시지 전송에 실패했습니다.");
    }

    return response.data.data;
  }

  /**
   * 메시지 내용 수정
   */
  async updateAdminMessage(
    companyId: number,
    messageId: number,
    request: ReplyOrderRevisionRequestRequest
  ): Promise<OrderRevisionSession> {
    const url = `/api/company/${companyId}/revision-requests/messages/${messageId}`;

    const response = await httpClient.put<{
      data: OrderRevisionSession;
      errorMsg: string | null;
      errorCode: string | null;
    }>(url, request);

    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "메시지 수정에 실패했습니다.");
    }

    return response.data.data;
  }

  /**
   * 메시지 삭제
   */
  async deleteAdminMessage(
    companyId: number,
    messageId: number
  ): Promise<OrderRevisionSession> {
    const url = `/api/company/${companyId}/revision-requests/messages/${messageId}`;

    const response = await httpClient.delete<{
      data: OrderRevisionSession;
      errorMsg: string | null;
      errorCode: string | null;
    }>(url);

    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "메시지 삭제에 실패했습니다.");
    }

    return response.data.data;
  }

  /**
   * 세션 재오픈(되돌리기) — sessionId 기반. closedAt=null, needsRework=false 초기화.
   * 같은 주문에 활성 세션이 이미 있으면 409 에러.
   */
  async reopenSession(
    companyId: number,
    sessionId: number
  ): Promise<OrderRevisionSession> {
    const url = `/api/company/${companyId}/revision-requests/${sessionId}/reopen`;
    const response = await httpClient.post<{
      data: OrderRevisionSession;
      errorMsg: string | null;
      errorCode: string | null;
    }>(url, {});
    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "처리에 실패했습니다.");
    }
    return response.data.data;
  }

  /**
   * 세션 종료/재작업 결정 — sessionId 기반.
   * needsRework=false면 "문의 종료", true면 "재작업 결정"
   */
  async closeSession(
    companyId: number,
    sessionId: number,
    request: CloseOrderRevisionSessionRequest
  ): Promise<OrderRevisionSession> {
    const url = `/api/company/${companyId}/revision-requests/${sessionId}/close`;

    const response = await httpClient.post<{
      data: OrderRevisionSession;
      errorMsg: string | null;
      errorCode: string | null;
    }>(url, request);

    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "처리에 실패했습니다.");
    }

    return response.data.data;
  }
}

export const companyOrderRevisionRequestApiService =
  new CompanyOrderRevisionRequestApiService();
